import { useState } from "react";
import { resetPassword, AUTH_TOKEN_KEY } from "@/lib/api";
import { Compass as CompassIcon, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";

// Password-reset screen. Rendered when the app URL carries ?reset=<token>
// (from the emailed reset link). On success, clears the token param and lets
// the user sign in with their new password.
export default function ResetPassword({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) { setError("Password must be at least 10 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      // ensure any stale session is cleared so the user signs in fresh
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not reset your password. The link may have expired.");
    }
    setBusy(false);
  };

  const goToApp = () => {
    window.location.href = "/?experience";
  };

  const inputCls = "w-full border border-stone-300 rounded-sm px-3 py-2.5 text-[14px] focus:border-[#8C3A2A] outline-none transition-colors";

  return (
    <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center px-6" data-testid="reset-password-screen">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-md shadow-sm p-8">
        <div className="flex items-center gap-2 text-[#8C3A2A] mb-1">
          <CompassIcon className="h-5 w-5" />
          <span className="font-mono-panel text-[11px] uppercase tracking-[0.18em]">The Writing Studio</span>
        </div>

        {done ? (
          <div className="text-center py-4" data-testid="reset-success">
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <h1 className="font-serif-display text-2xl text-stone-900 mb-1">Password updated</h1>
            <p className="text-[14px] text-stone-500 mb-6">You can now sign in with your new password.</p>
            <button
              onClick={goToApp}
              data-testid="reset-goto-signin"
              className="inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[13.5px] px-6 py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors"
            >
              Go to The Writing Studio
            </button>
          </div>
        ) : (
          <>
            <h1 className="font-serif-display text-2xl text-stone-900 mb-1">Choose a new password</h1>
            <p className="text-[13px] text-stone-500 mb-5">Enter a new password for your account.</p>
            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <label className="block text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1 font-mono-panel">New password</label>
                <input
                  type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="At least 10 characters"
                  data-testid="reset-password-input" className={inputCls + " pr-10"}
                />
                <button
                  type="button" onClick={() => setShow((s) => !s)}
                  data-testid="reset-password-toggle"
                  className="absolute right-2.5 top-[34px] text-stone-400 hover:text-[#8C3A2A] transition-colors"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1 font-mono-panel">Confirm password</label>
                <input
                  type={show ? "text" : "password"} required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password"
                  data-testid="reset-confirm-input" className={inputCls}
                />
              </div>
              {error && (
                <p data-testid="reset-error" className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{error}</p>
              )}
              <button
                type="submit" disabled={busy}
                data-testid="reset-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[13.5px] py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Reset password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
